import React, { useCallback } from 'react';
import Grid, { GridSize } from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import InfoIcon from '@mui/icons-material/Info';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  IImageSubSchema,
  IImageSubSchemaPartial,
} from '../../../../graphql/type/subSchema/image';
import useNotification from '../../_hook/useNotification';
import BaseImageUploadItem from './BaseImageUploadItem';

export interface IGridProps {
  gridXs?: GridSize;
  gridSm?: GridSize;
  gridMd?: GridSize;
  gridLg?: GridSize;
  gridXl?: GridSize;
}

interface IProps {
  uploadingCount?: number;
  onReorder?: (filenames: string[]) => void;
  onUpload?: (fileList: FileList | null) => void;
  onDelete?: (filename: string) => void;
  onPublish?: (filenames: string[]) => void;
  onUnpublish?: (filenames: string[]) => void;
  images: IImageSubSchemaPartial &
    {
      status: boolean;
      filename: string;
    }[];
  uploadInfo?: {
    ratio?: string;
    size?: string;
    acceptedUploadFileTypes?: string;
  };
  maximumUploadSize?: number;
  displayImageSize?: 'medium' | 'small' | 'large' | 'extraLarge' | 'original';
  color?: 'primary';
  gridXs?: IGridProps['gridXs'];
  gridSm?: IGridProps['gridSm'];
  gridMd?: IGridProps['gridMd'];
  gridLg?: IGridProps['gridLg'];
  gridXl?: IGridProps['gridXl'];
}

const useStyles = makeStyles<Theme, IProps>((theme: Theme) => ({
  infoPaper: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  dropzone: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: '2px',
    borderRadius: '2px',
    borderColor: ({ color }) =>
      color === 'primary' ? theme.palette.primary.main : '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#000',
    color: ({ color }) =>
      color === 'primary' ? theme.palette.primary.main : '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
  },
}));

export default function BaseImageUpload(props: IProps) {
  const {
    onDelete,
    onPublish,
    onUnpublish,
    onReorder,
    images,
    uploadingCount,
    uploadInfo,
    displayImageSize,
    maximumUploadSize,
    onUpload,
    gridXs,
    gridSm,
    gridMd,
    gridLg,
    gridXl,
  } = props;
  const { t } = useTranslation();
  const { notification } = useNotification();
  const classes = useStyles(props);
  const onDrop = useCallback((acceptedFiles) => {
    if (onUpload) {
      onUpload(acceptedFiles);
    }
  }, []);
  const options = {
    accept:
      uploadInfo && uploadInfo.acceptedUploadFileTypes
        ? uploadInfo.acceptedUploadFileTypes
        : undefined,
    validator: (file: File) => {
      const { size } = file;
      if (maximumUploadSize && file && size > maximumUploadSize) {
        notification.error(
          t('__fileName__is_over__size__limit', {
            fileName: file.name,
            size:
              uploadInfo && uploadInfo.size
                ? uploadInfo.size
                : maximumUploadSize,
          })
        );

        return {
          code: 'size-too-large',
          message: `Size is larger than ${maximumUploadSize}`,
        };
      }

      return null;
    },
    onDrop,
  };
  const {
    // acceptedFiles,
    // fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone(options);

  const SortableItem = SortableElement(
    ({ image }: { image: IImageSubSchema }) => (
      <Grid
        item
        key={image.filename}
        xs={gridXs || 12}
        sm={gridSm || 6}
        md={gridMd || 4}
        lg={gridLg || 3}
        xl={gridXl || 2}
      >
        <BaseImageUploadItem
          displayImageSize={displayImageSize}
          onDelete={onDelete}
          onPublish={onPublish}
          onUnpublish={onUnpublish}
          image={image}
        />
      </Grid>
    )
  );
  const SortableList = SortableContainer(() => (
    <Grid container item xs={12} spacing={1}>
      {images.map((image, index) => (
        <SortableItem key={image.filename} index={index} image={image} />
      ))}
    </Grid>
  ));

  return (
    <>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div {...getRootProps({ className: classes.dropzone })}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps()} />
        <Grid container justifyContent="center" spacing={1}>
          <Grid item xs={12} container justifyContent="center">
            <Typography variant="body1">
              {t('drop_file_here_or_click_to_browse_files')}
            </Typography>
          </Grid>
          {uploadInfo && (
            <>
              {uploadInfo.acceptedUploadFileTypes && (
                <Grid item xs={12} container justifyContent="center">
                  <Typography variant="body1">
                    ({t('only')} {uploadInfo.acceptedUploadFileTypes}{' '}
                    {t('image')})
                  </Typography>
                </Grid>
              )}
              {uploadInfo.ratio && (
                <Grid
                  item
                  xs={6}
                  container
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <AspectRatioIcon />
                  <Typography variant="caption">
                    &nbsp;{uploadInfo.ratio}
                  </Typography>
                </Grid>
              )}
              {uploadInfo.size && (
                <Grid
                  item
                  xs={6}
                  container
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <InfoIcon />
                  <Typography variant="caption">
                    &nbsp;{uploadInfo.size}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </div>
      <Grid container spacing={1}>
        {new Array(uploadingCount).fill(0).map((_, index) => (
          <Grid
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            item
            xs={gridXs || 12}
            sm={gridSm || 6}
            md={gridMd || 4}
            lg={gridLg || 3}
            xl={gridXl || 2}
          >
            <BaseImageUploadItem
              loading
              defaultLoadingHeight={images.length ? undefined : '200px'}
            />
          </Grid>
        ))}
        {onReorder ? (
          <SortableList
            distance={1}
            onSortEnd={({ oldIndex, newIndex }) => {
              if (oldIndex !== newIndex) {
                const fileNames = images.map((file) => file.filename);
                const movingFileName = fileNames[oldIndex];

                fileNames.splice(oldIndex, 1);
                fileNames.splice(newIndex, 0, movingFileName);
                onReorder(fileNames);
              }
            }}
            axis="xy"
          />
        ) : (
          images.map((image) => (
            <Grid
              item
              key={image.filename}
              xs={gridXs || 12}
              sm={gridSm || 6}
              md={gridMd || 4}
              lg={gridLg || 3}
              xl={gridXl || 2}
            >
              <BaseImageUploadItem
                displayImageSize={displayImageSize}
                onDelete={onDelete}
                onPublish={onPublish}
                onUnpublish={onUnpublish}
                image={image}
              />
            </Grid>
          ))
        )}
      </Grid>
    </>
  );
}
